import React, { useEffect, useState } from 'react';
import RialSymbol from '../components/RialSymbol';
import {
  Box, Card, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem,
  Paper, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Button,
} from '@mui/material';
import {
  Search as SearchIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

export default function ReturnsRefunds() {
  const [returns, setReturns] = useState<any[]>([]);
  const [refunds, setRefunds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'returns' | 'refunds'>('returns');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<'approve' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [returnTotal, setReturnTotal] = useState(0);
  const [refundTotal, setRefundTotal] = useState(0);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchData = async () => {
    try {
      const [returnsRes, refundsRes] = await Promise.all([
        axios.get('/api/returns/returns', { params: { page, limit: rowsPerPage } }),
        axios.get('/api/returns/refunds', { params: { page, limit: rowsPerPage } }),
      ]);
      setReturns(returnsRes.data.returns || []);
      setRefunds(refundsRes.data.refunds || []);
      setReturnTotal(returnsRes.data.total ?? returnsRes.data.returns?.length ?? 0);
      setRefundTotal(refundsRes.data.total ?? refundsRes.data.refunds?.length ?? 0);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleApproveReturn = async () => {
    try {
      await axios.put(`/api/returns/returns/${selectedItem._id}/approve`, {
        adminId: 'admin-id',
        notes: actionNotes,
      });
      fetchData();
      setActionDialog(null);
      setActionNotes('');
    } catch (error) {
      console.error('Error approving return:', error);
    }
  };

  const handleRejectReturn = async () => {
    try {
      await axios.put(`/api/returns/returns/${selectedItem._id}/reject`, {
        adminId: 'admin-id',
        rejectionReason: actionNotes,
      });
      fetchData();
      setActionDialog(null);
      setActionNotes('');
    } catch (error) {
      console.error('Error rejecting return:', error);
    }
  };

  const handleApproveRefund = async () => {
    try {
      await axios.put(`/api/returns/refunds/${selectedItem._id}/approve`, {
        adminId: 'admin-id',
        notes: actionNotes,
      });
      fetchData();
      setActionDialog(null);
      setActionNotes('');
    } catch (error) {
      console.error('Error approving refund:', error);
    }
  };

  const filteredReturns = returns.filter(ret => {
    const matchesSearch = ret.returnNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || ret.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredRefunds = refunds.filter(ref => {
    const matchesSearch = ref.refundNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ref.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || ref.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const returnStats = {
    total: returns.length,
    pending: returns.filter(r => r.status === 'pending').length,
    approved: returns.filter(r => r.status === 'approved').length,
    completed: returns.filter(r => r.status === 'completed').length,
    totalRefunded: returns.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.refundAmount || 0), 0),
  };

  const refundStats = {
    total: refunds.length,
    processing: refunds.filter(r => r.status === 'processing').length,
    completed: refunds.filter(r => r.status === 'completed').length,
    failed: refunds.filter(r => r.status === 'failed').length,
    totalRefunded: refunds.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.amount || 0), 0),
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Returns & Refunds</Typography>
          <Typography color="text.secondary">Manage customer returns and refunds.</Typography>
        </Box>
      </Box>

      {/* Tab Selector */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Button
          variant={activeTab === 'returns' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('returns')}
        >
          Returns
        </Button>
        <Button
          variant={activeTab === 'refunds' ? 'contained' : 'outlined'}
          onClick={() => setActiveTab('refunds')}
        >
          Refunds
        </Button>
      </Box>

      {/* Stats */}
      {activeTab === 'returns' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Returns</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{returnStats.total}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Pending</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{returnStats.pending}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Approved</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>{returnStats.approved}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Completed</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{returnStats.completed}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Refunded</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}><RialSymbol /> {returnStats.totalRefunded.toFixed(2)}</Typography>
          </Paper>
        </Box>
      )}

      {activeTab === 'refunds' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Refunds</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{refundStats.total}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Processing</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{refundStats.processing}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Completed</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{refundStats.completed}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Failed</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{refundStats.failed}</Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Total Refunded</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}><RialSymbol /> {refundStats.totalRefunded.toFixed(2)}</Typography>
          </Paper>
        </Box>
      )}

      {/* Filter and Search */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by ID or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              {activeTab === 'returns' && [
                <MenuItem key="pending" value="pending">Pending</MenuItem>,
                <MenuItem key="approved" value="approved">Approved</MenuItem>,
                <MenuItem key="completed" value="completed">Completed</MenuItem>,
                <MenuItem key="rejected" value="rejected">Rejected</MenuItem>,
              ]}
              {activeTab === 'refunds' && [
                <MenuItem key="initiated" value="initiated">Initiated</MenuItem>,
                <MenuItem key="processing" value="processing">Processing</MenuItem>,
                <MenuItem key="completed" value="completed">Completed</MenuItem>,
                <MenuItem key="failed" value="failed">Failed</MenuItem>,
              ]}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Returns Table */}
      {activeTab === 'returns' && (
        <>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Return ID</strong></TableCell>
                  <TableCell><strong>Order</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Refund Amount</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography>Loading returns...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredReturns.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No returns found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredReturns.map((ret) => (
                  <TableRow key={ret._id} hover>
                    <TableCell><Typography sx={{ fontWeight: 600 }}>{ret.returnNumber}</Typography></TableCell>
                    <TableCell>{ret.order?.orderNumber}</TableCell>
                    <TableCell>{ret.customer?.name}</TableCell>
                    <TableCell><Chip label={ret.reason} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip
                        label={ret.status}
                        color={ret.status === 'approved' ? 'success' : ret.status === 'rejected' ? 'error' : ret.status === 'completed' ? 'info' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell><RialSymbol /> {ret.refundAmount?.toFixed(2)}</TableCell>
                    <TableCell>{new Date(ret.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelectedItem(ret); setDialogOpen(true); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <PaginationControls page={page} total={returnTotal} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />
        </>
      )}

      {/* Refunds Table */}
      {activeTab === 'refunds' && (
        <>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell><strong>Refund ID</strong></TableCell>
                  <TableCell><strong>Order</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Method</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography>Loading refunds...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredRefunds.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No refunds found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredRefunds.map((ref) => (
                  <TableRow key={ref._id} hover>
                    <TableCell><Typography sx={{ fontWeight: 600 }}>{ref.refundNumber}</Typography></TableCell>
                    <TableCell>{ref.order?.orderNumber}</TableCell>
                    <TableCell>{ref.customer?.name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}><RialSymbol /> {ref.amount?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={ref.status}
                        color={ref.status === 'completed' ? 'success' : ref.status === 'failed' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell><Chip label={ref.paymentMethod} size="small" variant="outlined" /></TableCell>
                    <TableCell>{new Date(ref.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => { setSelectedItem(ref); setDialogOpen(true); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <PaginationControls page={page} total={refundTotal} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {activeTab === 'returns' ? 'Return Details' : 'Refund Details'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedItem && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">ID</Typography>
                <Typography sx={{ fontWeight: 600 }}>{selectedItem.returnNumber || selectedItem.refundNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Customer</Typography>
                <Typography>{selectedItem.customer?.name}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Order</Typography>
                <Typography>{selectedItem.order?.orderNumber}</Typography>
              </Box>
              {activeTab === 'returns' && (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Reason</Typography>
                    <Chip label={selectedItem.reason} size="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{selectedItem.description}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Refund Amount</Typography>
                    <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {selectedItem.refundAmount?.toFixed(2)}</Typography>
                  </Box>
                </>
              )}
              {activeTab === 'refunds' && (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Amount</Typography>
                    <Typography sx={{ fontWeight: 600 }}><RialSymbol /> {selectedItem.amount?.toFixed(2)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                    <Chip label={selectedItem.paymentMethod} size="small" />
                  </Box>
                </>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedItem.status}
                  color={activeTab === 'returns' ? (selectedItem.status === 'approved' ? 'success' : 'warning') : (selectedItem.status === 'completed' ? 'success' : 'warning')}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Date</Typography>
                <Typography>{new Date(selectedItem.createdAt).toLocaleString()}</Typography>
              </Box>

              {/* Action Notes */}
              {selectedItem.status === 'pending' && activeTab === 'returns' && actionDialog && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={actionDialog === 'approve' ? 'Approval Notes' : 'Rejection Reason'}
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  size="small"
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {activeTab === 'returns' && selectedItem?.status === 'pending' && (
            <>
              <Button onClick={() => setActionDialog('reject')} color="error" variant="outlined" size="small">
                Reject
              </Button>
              <Button onClick={() => setActionDialog('approve')} color="success" variant="contained" size="small">
                Approve
              </Button>
            </>
          )}
          {activeTab === 'refunds' && selectedItem?.status === 'initiated' && (
            <Button onClick={handleApproveRefund} color="success" variant="contained" size="small">
              Process Refund
            </Button>
          )}
          <Button onClick={() => { setDialogOpen(false); setActionDialog(null); setActionNotes(''); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Action Dialog */}
      <Dialog open={actionDialog !== null} onClose={() => setActionDialog(null)}>
        <DialogTitle>
          {actionDialog === 'approve' ? 'Approve Return' : 'Reject Return'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionDialog === 'approve' ? 'Approval Notes' : 'Rejection Reason'}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder="Enter notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog(null); setActionNotes(''); }}>Cancel</Button>
          <Button
            onClick={actionDialog === 'approve' ? handleApproveReturn : handleRejectReturn}
            variant="contained"
            color={actionDialog === 'approve' ? 'success' : 'error'}
          >
            {actionDialog === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
